/**
 * Pass 1 — Six default workspace dashboard cards.
 * Each card receives pre-fetched data from the parent and renders content or empty state.
 * No settings, no filters, no full-screen — those are Pass 3.
 */
import { Activity, AlertTriangle, CalendarClock, DollarSign, Layers, TrendingUp } from "lucide-react";
import { formatCalendarDate } from "@/utils/calendarDate";
import { UNAVAILABLE_TEXT, UnavailablePanel, UnavailableValue } from "@/components/ui/feedback/Unavailable";
import { DashboardCard, CardEmpty, CardLoading, type DashboardCardProps } from "./DashboardCard";
import {
  NOT_ENOUGH_DATA,
  hasKnownProjectState,
  hasLifecycleBeyondDraft,
  projectStateBadges,
} from "@/features/projects/projectStateDisplay";
import type {
  DashboardSummary,
  DashboardMilestone,
  DashboardRisksResponse,
  WorkspaceSummary,
  WorkspaceHealthData,
} from "../api";
import {
  ACTIVE_RISKS_SUBTEXT,
  ACTIVE_RISKS_TITLE,
} from "@/features/risks/activeRisksCount";

type CardActions = DashboardCardProps["actions"];

export const CARD_COULDNT_LOAD = "Couldn't load";
export const CARD_NO_DATA_YET = "No data yet";
/** RISK-COUNT-FE-1 — failed risk count; never imply empty. */
export const CARD_UNAVAILABLE = UNAVAILABLE_TEXT;
export { ACTIVE_RISKS_TITLE, ACTIVE_RISKS_SUBTEXT };
/** Empty copy only when count loaded and equals 0. */
export const ACTIVE_RISKS_EMPTY = "No risks logged yet.";

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isReadableProjectCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function hasProjectHealth(data: DashboardSummary): boolean {
  return hasLifecycleBeyondDraft(data.projectStateSummary);
}

/* ── 1. Project Health ── */

export function ProjectHealthCard({
  data,
  loading,
  failed = false,
  actions,
  onRetry,
}: {
  data: DashboardSummary | null;
  loading: boolean;
  failed?: boolean;
  actions?: CardActions;
  /** Reload the card data; passed only where the page has a real reload. */
  onRetry?: () => void;
}) {
  return (
    <DashboardCard title="Project Health" icon={<TrendingUp className="h-4 w-4" />} actions={actions}>
      {loading ? (
        <CardLoading />
      ) : failed || !data || !isReadableProjectCount(data.projectCount) ? (
        <UnavailablePanel onRetry={onRetry} testId="card-unavailable-project-health" />
      ) : data.projectCount === 0 ? (
        <CardEmpty message="No project health data yet." />
      ) : !hasKnownProjectState(data.projectStateSummary) ? (
        <CardEmpty message={NOT_ENOUGH_DATA} />
      ) : !hasProjectHealth(data) ? (
        <CardEmpty
          message={`${data.projectCount} ${data.projectCount === 1 ? "project" : "projects"} · no health data yet`}
        />
      ) : (
        <div className="space-y-3">
          <div className="text-3xl font-bold text-slate-900">{data.projectCount}</div>
          <p className="text-xs text-slate-500">projects in workspace</p>
          <div className="space-y-1.5 pt-1">
            {projectStateBadges(data.projectStateSummary).map((badge) => (
              <div key={badge.key} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${badge.dotClass}`} />
                  <span className="text-slate-600">{badge.label}</span>
                </div>
                <span className="font-medium text-slate-900">{badge.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}

/* ── 2. Tasks In Progress ── */

export function TasksInProgressCard({
  summary,
  health,
  loading,
  failed = false,
  actions,
  onRetry,
}: {
  summary: WorkspaceSummary | null;
  health: WorkspaceHealthData | null;
  loading: boolean;
  failed?: boolean;
  actions?: CardActions;
  /** Reload the card data; passed only where the page has a real reload. */
  onRetry?: () => void;
}) {
  const inProgress = health?.executionSummary?.counts?.inProgress;
  const total = summary?.tasksTotal ?? health?.executionSummary?.counts?.totalWorkItems;
  const completed = summary?.tasksCompleted ?? health?.executionSummary?.counts?.doneLast7Days;
  const flowEmpty = isCount(inProgress) && inProgress === 0 && (!isCount(total) || total === 0);

  return (
    <DashboardCard title="Tasks In Progress" icon={<Activity className="h-4 w-4" />} actions={actions}>
      {loading ? (
        <CardLoading />
      ) : failed || !isCount(inProgress) ? (
        <UnavailablePanel onRetry={onRetry} testId="card-unavailable-tasks-in-progress" />
      ) : flowEmpty ? (
        <CardEmpty message="No active task flow yet." />
      ) : (
        <div className="space-y-3">
          <div className="text-3xl font-bold text-slate-900">{inProgress}</div>
          <p className="text-xs text-slate-500">tasks in progress</p>
          <div className="mt-2 space-y-1.5 pt-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Total tasks</span>
              <span className="font-medium text-slate-900">{isCount(total) ? total : <UnavailableValue />}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Completed</span>
              <span className="font-medium text-emerald-600">{isCount(completed) ? completed : <UnavailableValue />}</span>
            </div>
          </div>
        </div>
      )}
    </DashboardCard>
  );
}

/* ── 3. Overdue Work ── */

export function OverdueWorkCard({
  health,
  loading,
  failed = false,
  actions,
  onRetry,
}: {
  health: WorkspaceHealthData | null;
  loading: boolean;
  failed?: boolean;
  actions?: CardActions;
  /** Reload the card data; passed only where the page has a real reload. */
  onRetry?: () => void;
}) {
  const count = health?.executionSummary?.counts?.overdueWorkItems;
  const items = health?.executionSummary?.topOverdue ?? [];

  return (
    <DashboardCard title="Overdue Work" icon={<AlertTriangle className="h-4 w-4" />} actions={actions}>
      {loading ? (
        <CardLoading />
      ) : failed || !isCount(count) ? (
        <UnavailablePanel onRetry={onRetry} testId="card-unavailable-overdue-work" />
      ) : count === 0 ? (
        <CardEmpty message="No overdue work." />
      ) : (
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-red-600">{count}</span>
            <span className="text-xs text-slate-500">overdue items</span>
          </div>
          <div className="space-y-2 pt-1">
            {items.slice(0, 5).map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-800">{item.title}</div>
                  <div className="truncate text-xs text-slate-400">{item.projectName}</div>
                </div>
                {item.dueDate && (
                  <span className="shrink-0 text-xs text-red-500">
                    {formatCalendarDate(item.dueDate)}
                  </span>
                )}
              </div>
            ))}
            {count > 5 && (
              <p className="text-xs text-slate-400">+{count - 5} more</p>
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}

/* ── 4. Upcoming Milestones ── */

export function UpcomingMilestonesCard({
  milestones,
  loading,
  failed = false,
  actions,
  onRetry,
}: {
  milestones: DashboardMilestone[] | null;
  loading: boolean;
  failed?: boolean;
  actions?: CardActions;
  /** Reload the card data; passed only where the page has a real reload. */
  onRetry?: () => void;
}) {
  return (
    <DashboardCard title="Upcoming Milestones" icon={<CalendarClock className="h-4 w-4" />} actions={actions}>
      {loading ? (
        <CardLoading />
      ) : failed || !milestones ? (
        <UnavailablePanel onRetry={onRetry} testId="card-unavailable-upcoming-milestones" />
      ) : milestones.length === 0 ? (
        <CardEmpty message="No upcoming milestones yet." />
      ) : (
        <div className="space-y-2">
          {milestones.slice(0, 6).map((ms) => (
            <div key={ms.id} className="flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-800">{ms.name}</div>
              </div>
              <span className="shrink-0 text-xs text-slate-500">
                {formatCalendarDate(ms.dueDate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
}

/* ── 5. Active risks (workspace dashboard tile; RISK-COUNT-FE-1 surface) ──
 * Data: GET /api/workspaces/:workspaceId/dashboard-data/risks → field `count`
 * (WorkspaceDashboardDataService.getOpenRisks). Same Active-risks definition as
 * Agent 3 activeRisksCount: work_risks status != CLOSED AND deleted_at IS NULL
 * (OPEN + MITIGATED + ACCEPTED). Portfolio/program cards read rollup
 * `totals.activeRisksCount` instead. */

export function OpenRisksCard({
  risks,
  loading,
  failed = false,
  actions,
  onRetry,
}: {
  risks: DashboardRisksResponse | null;
  loading: boolean;
  failed?: boolean;
  actions?: CardActions;
  /** Reload the card data; passed only where the page has a real reload. */
  onRetry?: () => void;
}) {
  const highSeverity = risks?.items?.filter((r) => r.severity === "HIGH" || r.severity === "CRITICAL") ?? [];

  return (
    <DashboardCard
      title={ACTIVE_RISKS_TITLE}
      icon={<AlertTriangle className="h-4 w-4" />}
      actions={actions}
    >
      {loading ? (
        <CardLoading />
      ) : failed || !risks || !isCount(risks.count) ? (
        <div data-testid="active-risks-unavailable">
          <UnavailablePanel onRetry={onRetry} />
        </div>
      ) : risks.count === 0 ? (
        <div data-testid="active-risks-empty">
          <CardEmpty message={ACTIVE_RISKS_EMPTY} />
        </div>
      ) : (
        <div className="space-y-3" data-testid="active-risks-count">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900">{risks.count}</span>
            <span className="text-xs text-slate-500">{ACTIVE_RISKS_SUBTEXT}</span>
          </div>
          {highSeverity.length > 0 && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {highSeverity.length} high severity
            </div>
          )}
          <div className="space-y-2 pt-1">
            {(risks.items ?? []).slice(0, 5).map((risk) => (
              <div key={risk.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-800">{risk.title}</span>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    risk.severity === "HIGH" || risk.severity === "CRITICAL"
                      ? "bg-red-100 text-red-700"
                      : risk.severity === "MEDIUM"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {risk.severity?.toLowerCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}

/* ── 6. Budget Status ──
 * Stabilization Pass 1 — this card has NO data source. There is no
 * budget aggregation endpoint and no budget table the workspace
 * dashboard can query. Until a real source ships, the card must not
 * present as a live "no data yet" empty state — that wording implies
 * "you just don't have data" and erodes trust when the user actually
 * has projects with budgets configured. The Coming soon badge is the
 * truthful presentation: the feature is not wired, the card is a
 * placeholder, no user action will populate it. */
export function BudgetStatusCard({ actions }: { actions?: CardActions }) {
  return (
    <DashboardCard title="Budget Status" icon={<DollarSign className="h-4 w-4" />} actions={actions}>
      <div className="flex h-full flex-col items-center justify-center gap-2 py-6 text-center">
        <span
          className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700"
          data-testid="budget-status-coming-soon"
        >
          Coming soon
        </span>
        <p className="max-w-[220px] text-xs text-slate-500">
          Workspace budget tracking is on the platform roadmap. No budget data is collected yet.
        </p>
      </div>
    </DashboardCard>
  );
}
